<script setup lang="ts">
import DialogProvider from "@/ui-plus/dialog/DialogProvider.vue";
import { Toaster } from "@/ui-plus/sonner";
import { toast } from '@/ui-plus/sonner'
import Logo from '@auth-frontend-vue/components/Logo.vue';
import { authState } from '@/ui-plus/useAuth';
import SettingsView from '@auth-frontend-vue/views/user/SettingsView.vue';
import AuthView from '@auth-frontend-vue/views/auth/AuthView.vue';
import AskView from '@auth-frontend-vue/views/user/AskView.vue';
import { LOCAL_STORAGE_KEYS } from '@auth-frontend-vue/const';
import { z } from 'zod';
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { useColorMode } from "@vueuse/core";
import { Moon, Sun } from "lucide-vue-next";
import { computed, ref, watch } from "vue";
import { goToEmailStep } from '@auth-frontend-vue/stores/authFlow.store';
import { showNameForm } from '@auth-frontend-vue/utils/nameValidation';
import { browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { isFreshSessionLoggedInAt } from '@lib/consts/authConsts';
import { authGlobalApiClient } from '@api-client/auth_global';
import { passkeyRegister } from '@auth-frontend-vue/lib/auth.actions';
import {
  canAutoAddPasskey,
  clearPasskeyUsedLocally,
  setPasskeyAutoAddPromptedAt,
  setPasskeyUsedLocally,
} from '@auth-frontend-vue/lib/passkeyLocalStorage';
import { maybePromptClaimEmail } from '@auth-frontend-vue/utils/claimEmailValidation';
import { maybePromptPhone } from '@auth-frontend-vue/utils/phoneValidation';

const mode = useColorMode();
const toasterTheme = computed(() => {
  if (mode.value === "auto") {
    return "system";
  } else {
    return mode.value;
  }
});

if (window.queryParams.remember_email) {
  // Only store if valid email format to prevent stored XSS from URL param
  const rememberEmailSchema = z.string().max(255).email();
  const parsed = rememberEmailSchema.safeParse(window.queryParams.remember_email);
  if (parsed.success) {
    localStorage.setItem(LOCAL_STORAGE_KEYS.REMEMBER_LAST_EMAIL, parsed.data);
  }
}

const queryParams = window.queryParams

/** Gate: AskView / SettingsView mount only after the 3 checks (name, passkey, email) are done. */
const checksDone = ref(false);
let isRunningChecks = false;

/** 1. Name is required. Loop until set (UpdateNameForm refreshes tokens on success). */
async function ensureName() {
  while (authState.value.accessTokenPayload && !authState.value.accessTokenPayload.name) {
    await showNameForm();
  }
}

/** 2. Soft passkey prompt: only if supported + no passkey + not asked in last 24h + fresh session. Cancel is ok. */
async function maybePromptPasskey() {
  const payload = authState.value.accessTokenPayload;
  if (!payload?.id) return;
  if (
    !browserSupportsWebAuthn() ||
    !canAutoAddPasskey(payload.id) ||
    !isFreshSessionLoggedInAt(payload.loggedInAt)
  ) {
    return;
  }

  toast.message('Add a passkey now! Next time, skip the password and log in faster.', {
    description: 'Future you will thank you. 😎',
    position: 'top-right',
    duration: 10000,
  });
  await new Promise(resolve => setTimeout(resolve, 200));
  toast.message('Add a passkey now! Next time, skip the password and log in faster.', {
    description: 'Future you will thank you. 😎',
    duration: 10000,
  });
  await new Promise(resolve => setTimeout(resolve, 200));
  const { data, error } = await (await authGlobalApiClient.passkey.passkeylist.$post()).json();
  if (error) return;

  const passkeys = data?.passkeys ?? [];
  if (passkeys.length > 0) {
    setPasskeyUsedLocally();
    return;
  }
  clearPasskeyUsedLocally();

  setPasskeyAutoAddPromptedAt(payload.id);
  await passkeyRegister(); // cancel / fail → still continue
}



watch(() => authState.value.accessTokenPayload, async (payload) => {
  if (payload === null) {
    goToEmailStep();
    checksDone.value = false;
    return;
  }
  if (isRunningChecks || checksDone.value) return;
  isRunningChecks = true;
  try {
    await ensureName();
    await maybePromptPhone();
    await maybePromptClaimEmail();
    await maybePromptPasskey();
    checksDone.value = true;
  } finally {
    isRunningChecks = false;
  }
}, { immediate: true });


</script>

<template>
  <div>
    <div class="mx-auto w-full sm:max-w-[600px]">
      <div>
        <Logo />
        <div v-if="authState.accessTokenPayload === null"
             class="container max-w-lg mx-auto px-4">
          <AuthView />
        </div>
        <!-- checks (name → passkey → email) run via dialogs; views mount only after all done -->
        <div v-else-if="checksDone">
          <AskView v-if="queryParams.type === 'ask'" />
          <SettingsView v-else-if="queryParams.type === 'settings'" />
        </div>
      </div>


      <DialogProvider />
      <Toaster :theme="toasterTheme"
               closeButton
               richColors
               class="pointer-events-auto" />

      <div class="mx-auto my-4 max-w-fit">

        <DropdownMenu>
          <DropdownMenuTrigger as-child>
            <Button variant="secondary"
                    size="icon"
                    class="rounded-full">
              <Moon class="block dark:hidden" />
              <Sun class="hidden dark:block" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem @click="mode = 'light'"> Light </DropdownMenuItem>
            <DropdownMenuItem @click="mode = 'dark'"> Dark </DropdownMenuItem>
            <DropdownMenuItem @click="mode = 'auto'"> System </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

      </div>
    </div>
  </div>
</template>
